if (!customElements.get('password-get-notified-form')) {
  customElements.define('password-get-notified-form', class PasswordGetNotifiedForm extends HTMLElement {
    constructor () {
      super();

      this.abortController = null;
      this.modal = null;
      this.form = null;
      this.originalSubmit = null;
      this.iti = null;
      this.countries = [];
      this.selectedCountry = null;
      this.successTimer = null;
      this.keyboardFrame = null;
      this.scrollLockY = 0;
      this.scrollLockTouchY = null;
      this.isGlobalScrollLocked = false;
      this.isNativeSubmitting = false;
      this.isRequestingCaptcha = false;
      this.isInline = false;
    }

    connectedCallback () {
      this.abortController = new AbortController();
      this.modal = this.closest('[data-modal]');
      this.isInline = this.dataset.inline === 'true' || !this.modal;
      this.form = this.querySelector('form');

      if (!this.form) return;

      this.cacheElements();
      this.initCountries();
      this.initPhoneInput();
      this.interceptProgrammaticSubmit();
      this.bindEvents();
      this.syncMessagePosition();

      if (this.hasServerSuccess()) {
        this.showSuccess(false);
      } else if (this.serverErrors?.textContent.trim()) {
        this.clearSubmitMarker();
        this.showStep('phone');
        this.setMessage(this.serverErrors.innerHTML, true);
      } else if (this.hasHashSuccessFallback()) {
        this.showSuccess(false);
      } else {
        this.showStep('email');
      }
    }

    disconnectedCallback () {
      this.abortController?.abort();
      this.clearSuccessTimer();
      // Temporarily disabled iOS keyboard visualViewport offset.
      // this.cancelKeyboardFrame();
      // this.resetKeyboardOffset();
      this.unlockGlobalScroll();
      this.iti?.destroy();

      if (this.form && this.originalSubmit) {
        this.form.submit = this.originalSubmit;
      }
    }

    cacheElements () {
      this.intro = this.querySelector('[data-form-intro]');
      this.legal = this.querySelector('[data-form-legal]');
      this.message = this.querySelector('[data-form-message]');
      this.emailStep = this.querySelector('[data-email-step]');
      this.phoneStep = this.querySelector('[data-phone-step]');
      this.thanksState = this.querySelector('[data-thanks-state]');
      this.emailInput = this.querySelector('[data-email-input]');
      this.emailSubmit = this.querySelector('[data-email-submit]');
      this.phoneInput = this.querySelector('[data-phone-input]');
      this.phoneSubmit = this.querySelector('[data-phone-submit]');
      this.normalizedPhoneInput = this.querySelector('[data-normalized-phone]');
      this.selectedCountryInput = this.querySelector('[data-selected-country]');
      this.countryToggle = this.querySelector('[data-country-toggle]');
      this.countryFlag = this.querySelector('[data-country-flag]');
      this.countryName = this.querySelector('[data-country-name]');
      this.countryDropdown = this.querySelector('[data-country-dropdown]');
      this.countrySearch = this.querySelector('[data-country-search]');
      this.countryList = this.querySelector('[data-country-list]');
      this.serverState = this.querySelector('[data-password-get-notified-form]');
      this.serverErrors = this.querySelector('[data-password-get-notified-server-errors]');
      this.closeButton = this.querySelector('[data-micromodal-close]');
      this.overlay = this.modal?.querySelector('.password-get-notified-modal__overlay');

      if (this.phoneSubmit) {
        this.phoneSubmit.dataset.label = this.phoneSubmit.getAttribute('aria-label') || '';
      }
    }

    bindEvents () {
      const { signal } = this.abortController;

      this.emailSubmit?.addEventListener('click', () => this.handleEmailSubmit(), { signal });
      this.emailInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.handleEmailSubmit();
      }, { signal });
      this.emailInput?.addEventListener('input', () => this.clearMessage(), { signal });
      this.emailInput?.addEventListener('input', () => this.syncLegalVisibility(), { signal });
      this.emailInput?.addEventListener('focusin', () => this.handleFormFieldFocus(), { signal });
      this.emailInput?.addEventListener('focusout', () => this.handleFormFieldBlur(), { signal });
      // Temporarily disabled iOS keyboard visualViewport offset.
      // this.emailInput?.addEventListener('focusin', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      // this.emailInput?.addEventListener('focusout', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      this.phoneInput?.addEventListener('input', () => this.clearMessage(), { signal });
      this.phoneInput?.addEventListener('focusin', () => this.handleFormFieldFocus(), { signal });
      this.phoneInput?.addEventListener('focusout', () => this.handleFormFieldBlur(), { signal });
      // this.phoneInput?.addEventListener('focusin', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      // this.phoneInput?.addEventListener('focusout', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      this.closeButton?.addEventListener('click', () => this.ensureModalCloses(), { signal });
      this.phoneInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        this.handlePhoneSubmit();
      }, { signal });
      this.phoneSubmit?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.handlePhoneSubmit();
      }, { signal });
      this.form.addEventListener('submit', (event) => this.handleFormSubmit(event), { signal });

      this.countryToggle?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleCountryDropdown();
      }, { signal });
      this.countrySearch?.addEventListener('input', () => this.renderCountries(this.countrySearch.value), { signal });
      this.countrySearch?.addEventListener('focusin', () => this.handleFormFieldFocus(), { signal });
      this.countrySearch?.addEventListener('focusout', () => this.handleFormFieldBlur(), { signal });
      // this.countrySearch?.addEventListener('focusin', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      // this.countrySearch?.addEventListener('focusout', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      this.countrySearch?.addEventListener('keydown', (event) => {
        event.stopPropagation();
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.countryDropdown || this.countryDropdown.hidden) return;
        if (this.countryToggle?.contains(event.target) || this.countryDropdown.contains(event.target)) return;
        this.closeCountryDropdown();
      }, { signal });
      document.addEventListener('touchstart', (event) => this.handleLockedTouchStart(event), { signal, passive: true });
      document.addEventListener('touchmove', (event) => this.handleLockedTouchMove(event), { signal, passive: false });
      // this.keyboardFrame and visualViewport offset are temporarily disabled.
      // window.visualViewport?.addEventListener('resize', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      // window.visualViewport?.addEventListener('scroll', () => this.scheduleKeyboardOffsetUpdate(), { signal });
      window.addEventListener('resize', () => {
        this.syncBackdropClose();
        this.syncMessagePosition();
        // this.scheduleKeyboardOffsetUpdate();
      }, { signal });
      document.addEventListener('micro:modal:show', (event) => this.handleModalShow(event), { signal });
      document.addEventListener('micro:modal:close', (event) => this.handleModalClose(event), { signal });
    }

    interceptProgrammaticSubmit () {
      this.originalSubmit = this.form.submit;
      this.form.submit = () => {
        return false;
      };
    }

    initCountries () {
      const countryData = window.intlTelInputGlobals?.getCountryData?.() || [];

      this.countries = countryData
        .filter((country) => country.iso2 && country.dialCode)
        .map((country) => ({
          ...country,
          search: `${country.name} ${country.iso2} ${country.dialCode}`.toLowerCase(),
        }));

      this.selectedCountry = this.findCountry(this.dataset.initialCountry) || this.findCountry('us') || this.countries[0] || null;
      this.renderCountries();
      this.updateCountryDisplay();
    }

    initPhoneInput () {
      if (!this.phoneInput || typeof window.intlTelInput !== 'function') return;

      this.iti = window.intlTelInput(this.phoneInput, {
        allowDropdown: false,
        autoPlaceholder: 'off',
        formatOnDisplay: false,
        initialCountry: this.selectedCountry?.iso2 || 'us',
        nationalMode: true,
        placeholderNumberType: 'MOBILE',
        utilsScript: this.dataset.utilsSrc,
      });
    }

    handleModalShow (event) {
      if (event.detail?.modal !== this.modal) return;

      if (this.isTouchMobile()) {
        this.lockGlobalScroll();
      } else {
        this.releaseModalScrollLock();
      }
      this.syncBackdropClose();
      // Temporarily disabled iOS keyboard visualViewport offset.
      // this.scheduleKeyboardOffsetUpdate();

      if (this.hasServerSuccess()) {
        this.showSuccess(true);
        return;
      }

      if (this.serverErrors?.textContent.trim()) {
        this.clearSubmitMarker();
        this.showStep('phone');
        this.focusElement(this.phoneInput);
        return;
      }

      if (this.hasHashSuccessFallback()) {
        this.showSuccess(true);
        return;
      }

      this.resetForm();
      this.focusElement(this.emailInput);
    }

    handleModalClose (event) {
      if (event.detail?.modal !== this.modal) return;

      if (this.dataset.step === 'success') {
        this.resetForm();
      }

      this.clearSuccessTimer();
      this.closeCountryDropdown();
      // this.resetKeyboardOffset();
      this.unlockGlobalScroll();
    }

    handleFormFieldFocus () {
      if (this.isInline) return;
      if (!this.isTouchMobile()) return;

      this.lockGlobalScroll();
    }

    handleFormFieldBlur () {
      if (this.isInline) return;

      window.setTimeout(() => {
        if (this.isFormInputFocused()) return;
        if (this.isTouchMobile() && this.modal?.getAttribute('aria-hidden') !== 'true') return;

        this.unlockGlobalScroll();
      }, 0);
    }

    lockGlobalScroll () {
      if (this.isGlobalScrollLocked) return;

      this.scrollLockY = window.scrollY || window.pageYOffset || 0;
      this.isGlobalScrollLocked = true;

      document.documentElement.classList.add('password-get-notified-scroll-locked');
      document.body.classList.add('password-get-notified-scroll-locked');
      document.body.style.setProperty('--password-get-notified-scroll-y', `${this.scrollLockY * -1}px`);
    }

    unlockGlobalScroll () {
      if (!this.isGlobalScrollLocked) return;

      const scrollY = this.scrollLockY;
      this.isGlobalScrollLocked = false;
      this.scrollLockY = 0;
      this.scrollLockTouchY = null;

      document.documentElement.classList.remove('password-get-notified-scroll-locked');
      document.body.classList.remove('password-get-notified-scroll-locked');
      document.body.style.removeProperty('--password-get-notified-scroll-y');
      window.scrollTo(0, scrollY);
    }

    handleLockedTouchStart (event) {
      if (!this.isGlobalScrollLocked) return;

      this.scrollLockTouchY = event.touches?.[0]?.clientY ?? null;
    }

    handleLockedTouchMove (event) {
      if (!this.isGlobalScrollLocked) return;

      const touchY = event.touches?.[0]?.clientY ?? null;
      const deltaY = this.scrollLockTouchY === null || touchY === null ? 0 : this.scrollLockTouchY - touchY;

      if (this.canScrollInsideLockedForm(event.target, deltaY)) {
        this.scrollLockTouchY = touchY;
        return;
      }

      event.preventDefault();
    }

    canScrollInsideLockedForm (target, deltaY) {
      const scrollable = target?.closest?.('.password-get-notified__country-dropdown, .password-get-notified__country-list');

      if (!scrollable || scrollable.hidden || scrollable.scrollHeight <= scrollable.clientHeight) return false;
      if (deltaY < 0 && scrollable.scrollTop <= 0) return false;
      if (deltaY > 0 && scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight) return false;

      return true;
    }

    syncBackdropClose () {
      if (!this.overlay) return;

      if (this.isTouchMobile()) {
        this.overlay.removeAttribute('data-micromodal-close');
      } else {
        this.overlay.setAttribute('data-micromodal-close', '');
      }
    }

    scheduleKeyboardOffsetUpdate () {
      if (this.modal?.getAttribute('aria-hidden') === 'true') return;
      if (this.keyboardFrame) return;

      this.keyboardFrame = window.requestAnimationFrame(() => {
        this.keyboardFrame = null;
        this.updateKeyboardOffset();
      });
    }

    updateKeyboardOffset () {
      const visualViewport = window.visualViewport;

      if (!visualViewport || !this.isFormInputFocused()) {
        this.resetKeyboardOffset();
        return;
      }

      const overlap = Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop);
      const safeGap = this.isTouchMobile() && overlap > 0 ? 64 : 0;
      const keyboardOffset = overlap + safeGap;

      this.modal?.style.setProperty('--password-get-notified-keyboard-offset', `${Math.ceil(keyboardOffset)}px`);
      this.modal?.style.setProperty('--password-get-notified-visual-height', `${Math.floor(visualViewport.height)}px`);
    }

    resetKeyboardOffset () {
      this.modal?.style.removeProperty('--password-get-notified-keyboard-offset');
      this.modal?.style.removeProperty('--password-get-notified-visual-height');
    }

    cancelKeyboardFrame () {
      if (!this.keyboardFrame) return;

      window.cancelAnimationFrame(this.keyboardFrame);
      this.keyboardFrame = null;
    }

    isFormInputFocused () {
      return document.activeElement === this.emailInput ||
        document.activeElement === this.phoneInput ||
        document.activeElement === this.countrySearch;
    }

    isTouchMobile () {
      return window.matchMedia('(hover: none), (pointer: coarse), (max-width: 767px)').matches;
    }

    ensureModalCloses () {
      window.setTimeout(() => {
        if (!this.modal || this.modal.getAttribute('aria-hidden') === 'true') return;

        this.closeModal();
      }, 0);
    }

    closeModal () {
      const modalId = this.modal?.id;
      const microModal = window.MicroModal || window.themeVendor?.MicroModal;

      if (modalId && typeof microModal?.close === 'function') {
        microModal.close(modalId);
        return;
      }

      this.modal?.classList.remove('is-open');
      this.modal?.setAttribute('aria-hidden', 'true');
      document.dispatchEvent(new CustomEvent('micro:modal:close', {
        detail: { modal: this.modal },
      }));
    }

    releaseModalScrollLock () {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.documentElement.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('padding-right');
    }

    handleEmailSubmit () {
      if (!this.emailInput) return;

      const email = this.emailInput.value.trim();

      if (!email) {
        this.setMessage(this.dataset.emailRequiredMessage);
        this.focusElement(this.emailInput);
        return;
      }

      if (!this.emailInput.checkValidity()) {
        this.setMessage(this.dataset.emailInvalidMessage);
        this.focusElement(this.emailInput);
        return;
      }

      this.clearMessage();
      this.showStep('phone');
      this.focusElement(this.phoneInput);
    }

    async handleFormSubmit (event) {
      if (this.isNativeSubmitting) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (this.isRequestingCaptcha) {
        return;
      }

      await this.handlePhoneSubmit();
    }

    async handlePhoneSubmit () {
      const isValid = await this.preparePhoneValue();
      if (!isValid) return;

      this.markSubmitAttempt();
      await this.submitWithCaptcha();
    }

    async preparePhoneValue () {
      const rawValue = this.phoneInput?.value.trim() || '';

      if (!rawValue) {
        this.setMessage(this.dataset.phoneRequiredMessage);
        this.focusElement(this.phoneInput);
        return false;
      }

      if (this.hasInternationalPrefix(rawValue)) {
        this.setMessage(this.dataset.phoneInternationalMessage);
        this.focusElement(this.phoneInput);
        return false;
      }

      await this.iti?.promise?.catch(() => null);
      this.iti?.setCountry(this.selectedCountry?.iso2 || 'us');

      if (!this.iti || !window.intlTelInputUtils || this.iti.isValidNumber() !== true) {
        this.setMessage(this.dataset.phoneInvalidMessage);
        this.focusElement(this.phoneInput);
        return false;
      }

      const normalized = this.iti.getNumber(window.intlTelInputUtils.numberFormat.E164);

      if (!normalized) {
        this.setMessage(this.dataset.phoneInvalidMessage);
        this.focusElement(this.phoneInput);
        return false;
      }

      this.normalizedPhoneInput.value = normalized;
      this.syncSelectedCountryField();
      this.clearMessage();

      return true;
    }

    submitWithCaptcha () {
      const captchaField = this.getCaptchaField();

      if (!captchaField) {
        this.submitAjax();
        return;
      }

      const existingToken = this.getCaptchaToken(captchaField);
      if (existingToken) {
        this.submitAjax(existingToken);
        return;
      }

      this.clearCaptchaToken(captchaField);
      this.isRequestingCaptcha = true;
      this.setLoading(true);

      const startedAt = Date.now();
      const waitForCaptcha = () => {
        const captchaToken = this.getCaptchaToken(captchaField);

        if (captchaToken) {
          this.isRequestingCaptcha = false;
          this.submitAjax(captchaToken);
          return;
        }

        if (Date.now() - startedAt > 8000) {
          this.isRequestingCaptcha = false;
          this.submitNative();
          return;
        }

        window.setTimeout(waitForCaptcha, 100);
      };

      this.form.requestSubmit();
      waitForCaptcha();
    }

    async submitAjax (captchaToken = null) {
      const action = this.form.getAttribute('action');

      if (!action) {
        this.submitNative();
        return;
      }

      this.setLoading(true);

      try {
        const data = new FormData(this.form);

        if (captchaToken) {
          data.set('h-captcha-response', captchaToken);
        }

        const response = await fetch(action, {
          method: 'POST',
          headers: {
            Accept: 'text/html, application/xhtml+xml',
          },
          body: data,
          credentials: 'same-origin',
        });

        if (response.status === 429) {
          this.clearSubmitMarker();
          this.setMessage(this.dataset.submitErrorMessage);
          return;
        }

        if (this.isSuccessfulCustomerRedirect(response.url)) {
          this.showSuccess(true);
          return;
        }

        const content = await response.text();

        if (!response.ok) {
          this.submitNative();
          return;
        }

        if (this.isChallengeResponse(content, response.url)) {
          window.location.href = response.url.includes('/challenge') ? response.url : action;
          return;
        }

        if (this.isCaptchaProtectedResponse(content, response.status)) {
          this.submitNative();
          return;
        }

        const result = this.parseAjaxResponse(content);

        if (result.success) {
          this.showSuccess(true);
        } else if (result.errors) {
          this.clearSubmitMarker();
          this.setMessage(result.errors, true);
        } else {
          this.submitNative();
        }
      } catch {
        this.submitNative();
      } finally {
        this.setLoading(false);
      }
    }

    parseAjaxResponse (content) {
      const doc = new DOMParser().parseFromString(content, 'text/html');
      const formState = doc.querySelector(`[data-password-get-notified-form][data-form-id="${this.escapeSelector(this.form.id)}"]`);

      if (!formState) return {};

      const errors = formState.querySelector('[data-password-get-notified-server-errors]');

      if (errors?.textContent.trim()) {
        return { errors: errors.innerHTML };
      }

      return {
        success: formState.dataset.serverSuccess === 'true',
      };
    }

    isSuccessfulCustomerRedirect (url = '') {
      if (!url) return false;

      try {
        return new URL(url, window.location.origin).searchParams.get('customer_posted') === 'true';
      } catch (error) {
        return url.includes('customer_posted=true');
      }
    }

    hasHashSuccessFallback () {
      if (window.location.hash !== `#${this.form.id}`) return false;

      const submittedAt = Number(this.getSubmitMarkerValue() || 0);
      const isRecentSubmit = submittedAt > 0 && Date.now() - submittedAt < 120000;

      if (isRecentSubmit) {
        this.clearSubmitMarker();
      }

      return isRecentSubmit;
    }

    markSubmitAttempt () {
      try {
        window.sessionStorage.setItem(this.getSubmitMarkerKey(), String(Date.now()));
      } catch {}
    }

    clearSubmitMarker () {
      try {
        window.sessionStorage.removeItem(this.getSubmitMarkerKey());
      } catch {}
    }

    getSubmitMarkerValue () {
      try {
        return window.sessionStorage.getItem(this.getSubmitMarkerKey());
      } catch {
        return null;
      }
    }

    getSubmitMarkerKey () {
      return `password-get-notified-submit:${this.form.id}`;
    }

    isChallengeResponse (content, responseUrl = '') {
      return responseUrl.includes('/challenge') ||
        content.includes('g-recaptcha') ||
        content.includes('hcaptcha') ||
        content.includes('id="challenge-form"') ||
        content.includes("id='challenge-form'") ||
        content.includes('Verifying your connection') ||
        content.includes('window._cf_chl_opt') ||
        content.includes('/cdn-cgi/challenge-platform') ||
        content.includes('__cf_chl');
    }

    isCaptchaProtectedResponse (content, status) {
      const normalizedContent = content.toLowerCase();

      return status === 429 ||
        (status === 400 && (
          normalizedContent.includes('missing captcha token') ||
          normalizedContent.includes('captcha') ||
          normalizedContent.includes('token') ||
          normalizedContent.includes('bad request')
        ));
    }

    getCaptchaField () {
      return this.form.querySelector('[data-hcaptcha-response], [name="h-captcha-response"], [name="g-recaptcha-response"]');
    }

    getCaptchaToken (captchaField) {
      return (captchaField?.value || captchaField?.getAttribute('data-hcaptcha-response') || '').trim();
    }

    clearCaptchaToken (captchaField) {
      if (!captchaField) return;

      if ('value' in captchaField) captchaField.value = '';
      captchaField.setAttribute('data-hcaptcha-response', '');
    }

    submitNative () {
      this.isNativeSubmitting = true;

      if (this.originalSubmit) {
        this.form.submit = this.originalSubmit;
      }

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        HTMLFormElement.prototype.submit.call(this.form);
      }
    }

    showStep (step) {
      const isEmail = step === 'email';
      const isPhone = step === 'phone';
      const isSuccess = step === 'success';

      this.emailStep.hidden = !isEmail;
      this.phoneStep.hidden = !isPhone;
      this.thanksState.hidden = !isSuccess;
      this.intro.hidden = isSuccess && !this.isInline;
      this.syncLegalVisibility(isSuccess);
      this.dataset.step = step;
      this.syncMessagePosition();

      if (!isPhone) this.closeCountryDropdown();
    }

    showSuccess (shouldAutoClose) {
      this.clearSubmitMarker();
      this.clearMessage();
      this.showStep('success');
      this.focusElement(this.thanksState);

      const delay = Number(this.dataset.autoCloseSeconds || 0);

      if ((!shouldAutoClose && !this.isInline) || delay <= 0) return;

      this.clearSuccessTimer();
      this.successTimer = window.setTimeout(() => {
        if (this.isInline) {
          this.resetForm();
          this.focusElement(this.emailInput);
          return;
        }

        this.closeButton?.click();
      }, delay * 1000);
    }

    resetForm () {
      this.clearSuccessTimer();
      this.clearMessage();
      this.form.reset();
      this.normalizedPhoneInput.value = '';
      if (this.serverState) this.serverState.dataset.serverSuccess = 'false';
      if (this.serverErrors) this.serverErrors.textContent = '';
      this.selectedCountry = this.findCountry(this.dataset.initialCountry) || this.findCountry('us') || this.countries[0] || null;
      this.iti?.setCountry(this.selectedCountry?.iso2 || 'us');
      this.updateCountryDisplay();
      this.renderCountries();
      this.syncLegalVisibility();
      this.showStep('email');
    }

    setLoading (isLoading) {
      if (this.phoneSubmit) this.phoneSubmit.disabled = isLoading;
      if (this.emailSubmit) this.emailSubmit.disabled = isLoading;

      if (!this.phoneSubmit) return;

      this.phoneSubmit.classList.toggle('is-loading', isLoading);
      this.phoneSubmit.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      if (isLoading && this.dataset.loadingMessage) {
        this.phoneSubmit.setAttribute('aria-label', this.dataset.loadingMessage);
      } else {
        this.phoneSubmit.setAttribute('aria-label', this.phoneSubmit.getAttribute('data-label') || '');
      }
    }

    setMessage (message, isHTML = false) {
      if (!this.message) return;

      this.syncMessagePosition();

      if (isHTML) {
        this.message.innerHTML = message || this.decodeHTML(this.dataset.submitErrorMessage);
      } else {
        this.message.textContent = this.decodeHTML(message || this.dataset.submitErrorMessage);
      }

      this.message.hidden = false;
    }

    syncMessagePosition () {
      if (!this.intro || this.intro.hidden) {
        this.style.removeProperty('--password-get-notified-message-top');
        return;
      }

      window.requestAnimationFrame(() => {
        if (!this.intro || this.intro.hidden) return;

        this.style.setProperty('--password-get-notified-message-top', `${Math.ceil(this.intro.offsetHeight + 4)}px`);
      });
    }

    decodeHTML (value = '') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }

    clearMessage () {
      if (!this.message) return;

      this.message.hidden = true;
      this.message.textContent = '';
    }

    hasServerSuccess () {
      return this.serverState?.dataset.serverSuccess === 'true';
    }

    syncLegalVisibility (forceHidden = false) {
      if (!this.legal) return;

      this.legal.hidden = forceHidden || !this.emailInput?.value.trim();
    }

    renderCountries (query = '') {
      if (!this.countryList) return;

      const search = query.trim().toLowerCase();
      const countries = search ? this.countries.filter((country) => country.search.includes(search)) : this.countries;

      this.countryList.textContent = '';
      this.countryList.scrollTop = 0;

      if (countries.length === 0) {
        const item = document.createElement('li');
        item.className = 'password-get-notified__country-empty';
        item.textContent = this.decodeHTML(this.dataset.noCountriesMessage || 'No countries found');
        this.countryList.appendChild(item);
        return;
      }

      countries.forEach((country) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const flag = document.createElement('span');
        const name = document.createElement('span');
        const dialCode = document.createElement('span');

        button.type = 'button';
        button.className = 'password-get-notified__country-option';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(country.iso2 === this.selectedCountry?.iso2));
        button.dataset.country = country.iso2;

        flag.className = 'password-get-notified__country-flag';
        flag.setAttribute('aria-hidden', 'true');
        this.renderFlagIcon(flag, country);

        name.className = 'password-get-notified__country-option-name';
        name.textContent = country.name;

        dialCode.className = 'password-get-notified__country-option-code';
        dialCode.textContent = `+${country.dialCode}`;

        button.append(flag, name, dialCode);
        button.addEventListener('click', () => this.selectCountry(country));
        item.appendChild(button);
        this.countryList.appendChild(item);
      });
    }

    selectCountry (country) {
      this.selectedCountry = country;
      this.iti?.setCountry(country.iso2);
      this.updateCountryDisplay();
      this.renderCountries(this.countrySearch?.value || '');
      this.closeCountryDropdown();
      this.focusElement(this.phoneInput);
    }

    updateCountryDisplay () {
      if (!this.selectedCountry) return;

      if (this.countryFlag) this.renderFlagIcon(this.countryFlag, this.selectedCountry);
      if (this.countryName) this.countryName.textContent = `${this.selectedCountry.name} +${this.selectedCountry.dialCode}`;
      this.syncSelectedCountryField();
    }

    syncSelectedCountryField () {
      if (!this.selectedCountryInput) return;

      this.selectedCountryInput.value = this.selectedCountry?.iso2?.toUpperCase() || '';
    }

    toggleCountryDropdown () {
      if (this.countryDropdown?.hidden) {
        this.openCountryDropdown();
      } else {
        this.closeCountryDropdown();
      }
    }

    openCountryDropdown () {
      this.countryDropdown.hidden = false;
      this.dataset.countryDropdownOpen = 'true';
      this.countryToggle?.setAttribute('aria-expanded', 'true');
      this.focusElement(this.countrySearch);
    }

    closeCountryDropdown () {
      if (!this.countryDropdown) return;

      this.countryDropdown.hidden = true;
      this.removeAttribute('data-country-dropdown-open');
      this.countryToggle?.setAttribute('aria-expanded', 'false');
    }

    hasInternationalPrefix (value) {
      const trimmed = value.trim();
      const digits = trimmed.replace(/\D/g, '');
      const dialCode = this.selectedCountry?.dialCode || '';

      return trimmed.startsWith('+') || trimmed.startsWith('00') || Boolean(dialCode && digits.startsWith(dialCode));
    }

    findCountry (iso2) {
      if (!iso2) return null;

      const normalized = iso2.toLowerCase();

      return this.countries.find((country) => country.iso2 === normalized) || null;
    }

    getFlagIconUrl (iso2) {
      if (!iso2) return '';

      return `https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/4.1.4/flags/4x3/${iso2.toLowerCase()}.svg`;
    }

    renderFlagIcon (element, country) {
      if (!element || !country?.iso2) return;

      const image = document.createElement('img');
      image.className = 'password-get-notified__country-flag-image';
      image.alt = '';
      image.decoding = 'async';
      image.loading = 'lazy';
      image.src = this.getFlagIconUrl(country.iso2);
      image.addEventListener('error', () => {
        element.textContent = this.getFlagEmoji(country.iso2);
      }, { once: true });

      element.textContent = '';
      element.appendChild(image);
    }

    getFlagEmoji (iso2) {
      if (!iso2 || iso2.length !== 2) return '';

      return iso2
        .toUpperCase()
        .split('')
        .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
        .join('');
    }

    focusElement (element) {
      if (this.isTouchMobile() && element?.matches?.('input, textarea, select')) return;

      window.setTimeout(() => {
        element?.focus?.();
      }, 50);
    }

    clearSuccessTimer () {
      if (!this.successTimer) return;

      window.clearTimeout(this.successTimer);
      this.successTimer = null;
    }

    escapeSelector (value) {
      if (window.CSS?.escape) return window.CSS.escape(value);

      return value.replace(/"/g, '\\"');
    }
  });
}
